/**
 * Smart placeholder utilities for images and content
 */

// Generate a data URL for a colored placeholder
export function generatePlaceholderDataUrl(
  width: number = 400,
  height: number = 400,
  color: string = "E9D5FF"
): string {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#${color}"/>
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="24" fill="#9333EA" text-anchor="middle" dominant-baseline="middle">💎</text>
    </svg>
  `.trim();
  // Encode SVG for data URL (browser-compatible)
  const encoded = encodeURIComponent(svg);
  return `data:image/svg+xml,${encoded}`;
}

// Get smart placeholder based on product type/name
export function getSmartProductPlaceholder(productName?: string, productType?: string): string {
  // If we have a product name, generate a consistent placeholder based on it
  if (productName) {
    const hash = productName.split("").reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    // Generate colors based on product name hash
    const colors = [
      "E9D5FF", // Purple
      "FCE7F3", // Pink
      "FEF3C7", // Yellow
      "DBEAFE", // Blue
      "D1FAE5", // Green
      "FED7AA", // Orange
    ];
    const color = colors[Math.abs(hash) % colors.length];
    return generatePlaceholderDataUrl(400, 400, color);
  }
  
  // Default based on product type
  const typeColors: Record<string, string> = {
    bridal: "FCE7F3", // Pink
    workwear: "DBEAFE", // Blue
    mehndi: "FEF3C7", // Yellow
    heritage: "FED7AA", // Orange
    dailywear: "D1FAE5", // Green
  };
  
  const color = typeColors[productType?.toLowerCase() || ""] || "E9D5FF";
  return generatePlaceholderDataUrl(400, 400, color);
}

// Get placeholder for customer avatars
export function getCustomerPlaceholder(name?: string): string {
  if (name) {
    const hash = name.split("").reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    const colors = ["E0E7FF", "FCE7F3", "DBEAFE", "D1FAE5"];
    const color = colors[Math.abs(hash) % colors.length];
    return generatePlaceholderDataUrl(200, 200, color);
  }
  return generatePlaceholderDataUrl(200, 200, "E0E7FF");
}

// Smart input placeholders
export const inputPlaceholders = {
  email: "your.email@example.com",
  phone: "+91 98765 43210",
  name: "Enter your full name",
  address: "Enter your complete address",
  city: "Mumbai",
  state: "Maharashtra",
  pincode: "400001",
  search: "Search for jewellery, collections, or products...",
  minPrice: "₹0",
  maxPrice: "₹1,00,000",
  quantity: "1",
  message: "Tell us how we can help you...",
  subject: "What is this regarding?",
};

// Smart empty state messages
export const emptyStates = {
  products: {
    title: "No products found",
    description: "Try adjusting your filters or browse our collections",
    action: "Browse Collections",
  },
  cart: {
    title: "Your cart is empty",
    description: "Looks like you haven't added anything to your cart yet",
    action: "Start Shopping",
  },
  wishlist: {
    title: "Your wishlist is empty",
    description: "Start adding items you love to your wishlist!",
    action: "Start Shopping",
  },
  orders: {
    title: "No orders yet",
    description: "Your order history will appear here once you make a purchase",
    action: "Browse Products",
  },
  reviews: {
    title: "No reviews yet",
    description: "Be the first to review this product",
    action: "Write a Review",
  },
};

